import { useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { getPosition } from '../lib/native';
import { Navigation } from 'lucide-react';
import { InlineIcon } from './InlineIcon';
import { BACK_TO_START, RouteEndpoints, type RouteOptions } from './RouteEndpoints';

interface Result {
  planned: number;
  segments: number;
  maxPerSegment: number;
  totalSeconds: number;
  totalMeters: number;
  stopMinutes: number;
  skipped: Array<{ id: string; orderNo: string }>;
  startLabel?: string;
  endLabel?: string;
}

/**
 * 智能推荐路线。
 *
 * ★ 要解决的事:司机手上二十单,先送哪家全靠自己猜。猜错的代价是
 *   同一条路来回跑两趟 —— 而他跑完才知道。
 *
 * ★ 为什么顺序存在**服务端**而不是本地缓存:他要按这个顺序干一整天,
 *   中间会刷新、会杀后台、手机可能换一台。存本地的话每次都得重点一次,
 *   而每一次都是一笔真金白银的 Google 调用。
 *
 * ★ 两个口径不是「最快 / 最短」。实测发现 Google 的 Routes API
 *   **只按时间优化**,没有「最短距离」这个模式;而且要让它排顺序就
 *   不能用最精确的那一档路况(两者互斥)。所以给的是
 *   「看实时路况 / 按常规路况」—— 照实说比起一个做不到的名字好。
 */
export function RoutePlanBar({
  count,
  route,
  onPlanned,
}: {
  /** 手上要送的单数 —— 一单都没有时这一条不出现 */
  count: number;
  route: { plannedAt: string; planned: number; unplanned: number } | null | undefined;
  onPlanned: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  /** 起点 / 终点的选项 —— 每次展开面板时现拉(取货点跟着手上的单变) */
  const [options, setOptions] = useState<RouteOptions | null>(null);
  const [start, setStart] = useState<'PICKUP' | 'CURRENT'>('CURRENT');
  const [endId, setEndId] = useState<string>(BACK_TO_START);

  async function toggle() {
    if (open) return setOpen(false);
    setOpen(true);
    setErr(null);
    try {
      const o = await api<RouteOptions>('/driver/route/options');
      setOptions(o);
      // 起点默认现在的位置(要从取货点排得自己选);终点默认那个存好的默认终点
      setStart('CURRENT');
      setEndId(o.places.find((p) => p.isDefault)?.id ?? BACK_TO_START);
    } catch (e) {
      // 拿不到选项也让他照原来的方式排(现在的位置出发、回到起点)
      setOptions({ pickup: null, pickupCount: 0, places: [] });
      setErr(e instanceof ApiFailure ? e.message : null);
    }
  }

  if (count === 0) return null;

  async function plan(traffic: 'AWARE' | 'UNAWARE') {
    if (busy) return;
    setBusy(traffic);
    setErr(null);
    try {
      /*
       * ★ 起点在**这一刻**从手机拿,不靠服务端那份。
       *
       *   服务端 `driver.last_lat` 是位置上报写的,而位置上报在司机端
       *   还没实现 —— 生产上九个司机那一列全是空的,于是这个按钮对谁都
       *   报「拿不到你的当前位置」。按钮本来就是他点的,此刻取一次 GPS
       *   最直接,也最新鲜。
       *
       * ★ 拿不到也照样发(传 undefined),让服务端去退回库里那份、
       *   或者给出那句说得清的报错 —— 不要在这里先替他判断失败。
       */
      const pos = await getPosition();
      setResult(
        await api<Result>('/driver/route/optimize', {
          method: 'POST',
          body: {
            traffic,
            ...(pos ? { lat: pos.lat, lng: pos.lng } : {}),
            start,
            ...(endId !== BACK_TO_START ? { endPlaceId: endId } : {}),
          },
        }),
      );
      setOpen(false);
      await onPlanned();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '算不出路线');
    } finally {
      setBusy(null);
    }
  }

  async function clear() {
    if (busy) return;
    setBusy('CLEAR');
    try {
      await api('/driver/route/clear', { method: 'POST' });
      setResult(null);
      setOpen(false);
      await onPlanned();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '清不掉');
    } finally {
      setBusy(null);
    }
  }

  const stale = (route?.unplanned ?? 0) > 0;

  return (
    <div className="mx-4 mt-3">
      <div className="flex items-center gap-2">
        <button
          onClick={() => void toggle()}
          className={`h-11 flex-1 rounded-xl text-sm font-medium ${
            stale
              ? 'bg-amber-500 text-white'
              : route
                ? 'border border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-300'
                : 'bg-brand-600 text-white'
          }`}
        >
          {/*
            ★ 按钮本身要说出「现在是什么状态」。
              规划过了还写「智能推荐路线」的话,司机不知道该不该再点;
              而有新单没排进去时,这个按钮是他唯一会看的地方。
          */}
          {stale
            ? `有 ${route!.unplanned} 单不在顺序里 —— 重新规划`
            : route
              ? `已按推荐顺序(${route.planned} 单)· 重新规划`
              : <><InlineIcon icon={Navigation} /> 智能推荐路线</>}
        </button>
        {route && (
          <button
            onClick={() => void clear()}
            disabled={busy !== null}
            className="h-11 px-3 rounded-xl border border-slate-300 text-xs text-slate-500
                       dark:border-slate-600"
          >
            取消顺序
          </button>
        )}
      </div>

      {err && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{err}</p>}

      {result && (
        <div className="mt-2 rounded-xl bg-slate-100 p-3 text-sm dark:bg-slate-800">
          <div className="text-slate-900 dark:text-slate-100">
            排好了 {result.planned} 单 · 约 {Math.round(result.totalSeconds / 60)} 分钟 ·{' '}
            {(result.totalMeters / 1000).toFixed(1)} 公里
          </div>
          {result.startLabel && (
            <div className="mt-0.5 text-xs text-slate-500">
              从 {result.startLabel} 出发 → 终点 {result.endLabel}
            </div>
          )}
          <div className="mt-0.5 text-xs text-slate-500">
            已经按每单 {result.stopMinutes} 分钟的停留算进去了。
          </div>
          {/*
            ★ 分段了必须说。超过 25 单是一段段分别优化的,不是全局最优 ——
              不说的话司机会觉得系统算得不对,然后再也不用这个功能。
          */}
          {result.segments > 1 && (
            <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              超过 {result.maxPerSegment} 单了,是分 {result.segments} 段分别优化的 ——
              每段内部最优,段之间不保证。
            </div>
          )}
          {result.skipped.length > 0 && (
            <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              {result.skipped.length} 单没有坐标排不进去(地址待复核):
              {result.skipped.map((s) => s.orderNo).join('、')}
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="mt-2 space-y-2 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
          {options ? (
            <RouteEndpoints
              options={options}
              onPlacesChanged={(places) => setOptions((o) => (o ? { ...o, places } : o))}
              start={start}
              setStart={setStart}
              endId={endId}
              setEndId={setEndId}
            />
          ) : (
            <p className="text-sm text-slate-400">读取中…</p>
          )}
          <button
            onClick={() => void plan('AWARE')}
            disabled={busy !== null}
            className="w-full rounded-lg bg-brand-600 px-3 py-2 text-left text-white disabled:opacity-40"
          >
            <span className="block text-sm font-medium">
              {busy === 'AWARE' ? '算着…' : '看实时路况'}
            </span>
            <span className="block text-xs opacity-80">按现在的堵车情况排 —— 慢一点,但更准</span>
          </button>
          <button
            onClick={() => void plan('UNAWARE')}
            disabled={busy !== null}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-left
                       dark:border-slate-600 disabled:opacity-40"
          >
            <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
              {busy === 'UNAWARE' ? '算着…' : '按常规路况'}
            </span>
            <span className="block text-xs text-slate-500">不看当前堵车 —— 算得快</span>
          </button>
        </div>
      )}
    </div>
  );
}
